import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { appsAPI } from '../api/appsAPI';
import { useAuth } from '../../auth/hooks/useAuth';
import {
  resolveAppLaunchTarget,
  navigateAppLaunch,
  toExamSlug,
} from '../utils/appDisplayUtils';
import './AllAppsInternalPage.css';

const CDN = 'https://pathbuddy-media.lon1.cdn.digitaloceanspaces.com/tutorials';

/** Full internal catalogue — Apps only (no Cases). */
const INTERNAL_APPS = [
  { exam_name: 'caius_explore', title: 'Caius Explore', category: 'Interview', image: `${CDN}/caius_explore/caius_explore-260x146.png` },
  { exam_name: 'medicine_mmi_interview', title: 'MMI', category: 'Interview', image: `${CDN}/medicine_mmi_interview/medicine_mmi_interview-260x146.png` },
  { exam_name: 'medicine_panel_interview', title: 'Panel', category: 'Interview', image: `${CDN}/medicine_panel_interview/medicine_panel_interview-260x146.png` },
  { exam_name: 'oxbridge_interview', title: 'Oxbridge Interview', category: 'Interview', image: `${CDN}/oxbridge_interview/oxbridge_interview-260x146.png` },
  { exam_name: 'interview_simulator', title: 'Interview Simulator', category: 'Interview', image: `${CDN}/interview_simulator/interview_simulator-260x146.png` },
  { exam_name: 'investment_banking_interview', title: 'Investment Banking Interview', category: 'Interview', image: `${CDN}/investment_banking_interview/investment_banking_interview-260x146.png` },
  { exam_name: 'private_equity_interview', title: 'Private Equity Interview', category: 'Interview', image: `${CDN}/private_equity_interview/private_equity_interview-260x146.png` },
  { exam_name: 'engineering_interview', title: 'Engineering Interview', category: 'Interview', image: `${CDN}/engineering_interview/engineering_interview-260x146.png` },
  { exam_name: 'computer_science_interview', title: 'Computer Science Interview', category: 'Interview', image: `${CDN}/computer_science_interview/computer_science_interview-260x146.png` },
  { exam_name: 'law_interview', title: 'Law Interview', category: 'Interview', image: `${CDN}/law_interview/law_interview-260x146.png` },
  { exam_name: 'operations_fs_interview', title: 'Operations Interview (FS)', category: 'Interview', image: `${CDN}/operations_fs_interview/operations_fs_interview-260x146.png` },
  { exam_name: 'scholarship_interview', title: 'Scholarship Interview', category: 'Interview', image: `${CDN}/scholarship_interview/scholarship_interview-260x146.png` },
  // Live Interview workspaces — real mock interviews from interview-papers, distinct
  // from the tools above (which point at the older /website-interview service).
  // Not in LAUNCH_BY_EXAM: resolveAppLaunchTarget falls through to this path field.
  { exam_name: 'live_interview_medicine', title: 'Medicine Interviews', category: 'Interview', path: '/interviews/medicine', image: `${CDN}/medicine_mmi_interview/medicine_mmi_interview-260x146.png` },
  { exam_name: 'live_interview_dentistry', title: 'Dentistry Interviews', category: 'Interview', path: '/interviews/dentistry', image: `${CDN}/medicine_panel_interview/medicine_panel_interview-260x146.png` },
  { exam_name: 'live_interview_oxbridge', title: 'Oxbridge Interviews', category: 'Interview', path: '/interviews/oxbridge', image: `${CDN}/oxbridge_interview/oxbridge_interview-260x146.png` },
  { exam_name: 'medical_interviews', title: 'Medical Interviews', category: 'Exam', image: `${CDN}/medicine_mmi_interview/medicine_mmi_interview-260x146.png` },
  { exam_name: 'lnat', title: 'LNAT', category: 'Exam', image: `${CDN}/lnat/lnat-260x146.png` },
  { exam_name: 'tmua', title: 'TMUA', category: 'Exam', image: `${CDN}/tmua/tmua-260x146.png` },
  { exam_name: 'ucat', title: 'UCAT', category: 'Exam', image: `${CDN}/ucat/ucat-260x146.png` },
  { exam_name: 'esat', title: 'ESAT', category: 'Exam', image: `${CDN}/engineering_interview/engineering_interview-260x146.png` },
  { exam_name: 'tara', title: 'TARA', category: 'Exam', image: `${CDN}/pattern_recognition/pattern_recognition-260x146.png` },
  { exam_name: 'logical_reasoning', title: 'Logical Reasoning', category: 'Reasoning', image: `${CDN}/logical_reasoning/logical_reasoning-260x146.png` },
  { exam_name: 'pattern_recognition', title: 'Pattern Recognition', category: 'Reasoning', image: `${CDN}/pattern_recognition/pattern_recognition-260x146.png` },
  { exam_name: 'numerical_reasoning', title: 'Numerical Reasoning', category: 'Reasoning', image: `${CDN}/numerical_reasoning/numerical_reasoning-260x146.png` },
  { exam_name: 'verbal_reasoning', title: 'Verbal Reasoning', category: 'Reasoning', image: `${CDN}/verbal_reasoning/verbal_reasoning-260x146.png` },
  { exam_name: 'cv', title: 'CV Builder', category: 'Utility Apps', image: `${CDN}/cv/cv-260x146.png` },
  { exam_name: 'cover_letter', title: 'Cover Letter Builder', category: 'Utility Apps', image: `${CDN}/cover_letter/cover_letter-260x146.png` },
  { exam_name: 'personal_statement', title: 'Personal Statement Builder', category: 'Utility Apps', image: `${CDN}/personal_statement/personal_statement-260x146.png` },
  { exam_name: 'library', title: 'Smart Library', category: 'Utility Apps', image: `${CDN}/library/library-260x146.png` },
  { exam_name: 'supercurricular_books', title: 'Supercurricular Books', category: 'Utility Apps', image: `${CDN}/library/library-260x146.png` },
  { exam_name: 'code_lab', title: 'Code Lab', category: 'Productivity Apps', image: `${CDN}/code_lab/code_lab-260x146.png` },
  { exam_name: 'financial_modeling_lab', title: 'Financial Modeling Lab', category: 'Productivity Apps', image: `${CDN}/financial_modeling_lab/financial_modeling_lab-260x146.png` },
  { exam_name: 'flowforge', title: 'FlowForge', category: 'Productivity Apps', image: '/flowforge_icon.png' },
  { exam_name: 'custom_quiz_generator', title: 'Custom Quiz Generator', category: 'Productivity Apps', image: '/custom-quiz-generator-icon.svg' },
  { exam_name: 'flash_cards', title: 'Flash Cards', category: 'Productivity Apps', image: `${CDN}/flash_cards/flash_cards-260x146.png` },
  { exam_name: 'notes', title: 'Notes', category: 'Productivity Apps', image: `${CDN}/notes/notes-260x146.png` },
];

function imageFor(app) {
  if (app.image) return app.image;
  const imgs = app.images || {};
  return imgs.medium || imgs.large || imgs.small || '/logo-yellow.png';
}

export default function AllAppsInternalPage() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [apiApps, setApiApps] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = 'All Apps (internal)';
    let robots = document.querySelector('meta[name="robots"]');
    if (!robots) {
      robots = document.createElement('meta');
      robots.setAttribute('name', 'robots');
      document.head.appendChild(robots);
    }
    robots.setAttribute('content', 'noindex, nofollow');
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await appsAPI.getAppsPublic();
        const list = Array.isArray(data) ? data : [];
        if (!cancelled) setApiApps(list);
      } catch (_) {
        if (!cancelled) setApiApps([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const apps = useMemo(() => {
    const byExam = new Map();

    INTERNAL_APPS.forEach((app) => {
      byExam.set(toExamSlug(app.exam_name), { ...app });
    });

    apiApps.forEach((app) => {
      const type = (app.app_type || '').toLowerCase();
      if (type === 'case' || type === 'case_study') return;

      const key = toExamSlug(app.exam_name) || toExamSlug(app.title);
      if (!key || key === 'uai') return;

      const probe = {
        ...app,
        exam_name: toExamSlug(app.exam_name) || app.exam_name,
      };
      if (!resolveAppLaunchTarget(probe)) return;

      const existing = byExam.get(key);
      if (existing) {
        byExam.set(key, {
          ...existing,
          id: app.id || existing.id,
          images: app.images || existing.images,
          app_type: app.app_type || existing.app_type,
          subject: app.subject || existing.subject,
          has_logical_reasoning: app.has_logical_reasoning || existing.has_logical_reasoning,
          has_pattern_recognition: app.has_pattern_recognition || existing.has_pattern_recognition,
          has_numerical_reasoning: app.has_numerical_reasoning || existing.has_numerical_reasoning,
          has_verbal_reasoning: app.has_verbal_reasoning || existing.has_verbal_reasoning,
          exam_name: existing.exam_name || toExamSlug(app.exam_name),
        });
        return;
      }

      byExam.set(key, {
        exam_name: probe.exam_name,
        title: app.title || key,
        category: 'Other',
        id: app.id,
        images: app.images,
        app_type: app.app_type,
        subject: app.subject,
        image: imageFor(app),
        has_logical_reasoning: app.has_logical_reasoning,
        has_pattern_recognition: app.has_pattern_recognition,
        has_numerical_reasoning: app.has_numerical_reasoning,
        has_verbal_reasoning: app.has_verbal_reasoning,
      });
    });

    return Array.from(byExam.values()).sort((a, b) => {
      const cat = (a.category || '').localeCompare(b.category || '');
      if (cat !== 0) return cat;
      return (a.title || '').localeCompare(b.title || '');
    });
  }, [apiApps]);

  const grouped = useMemo(() => {
    return apps.reduce((acc, app) => {
      const cat = app.category || 'Other';
      if (!acc[cat]) acc[cat] = [];
      acc[cat].push(app);
      return acc;
    }, {});
  }, [apps]);

  const goTo = useCallback(
    (target) => {
      if (!target) return;
      if (!isAuthenticated) {
        navigate('/login', { state: { returnTo: target } });
        return;
      }
      navigateAppLaunch(navigate, target);
    },
    [isAuthenticated, navigate]
  );

  const handleOpen = useCallback(
    (app) => {
      const target = resolveAppLaunchTarget(app);
      if (target) {
        goTo(target);
        return;
      }
      console.warn('[all-apps] No launch target for', app?.exam_name || app?.title);
    },
    [goTo]
  );

  return (
    <div className="all-apps-internal">
      <header className="all-apps-internal__header">
        <p className="all-apps-internal__badge">Internal · not linked in nav</p>
        <h1>All Apps</h1>
        <p className="all-apps-internal__sub">
          {loading ? 'Loading…' : `${apps.length} apps (no cases)`}
          {!isAuthenticated ? ' · login required to open tools' : ''}
        </p>
      </header>

      {Object.entries(grouped).map(([category, list]) => (
        <section key={category} className="all-apps-internal__section">
          <h2>{category}</h2>
          <div className="all-apps-internal__grid">
            {list.map((app) => (
              <button
                key={toExamSlug(app.exam_name) || app.id || app.title}
                type="button"
                className="all-apps-internal__tile"
                onClick={() => handleOpen(app)}
              >
                <span className="all-apps-internal__icon">
                  <img
                    src={imageFor(app)}
                    alt=""
                    draggable={false}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = '/logo-yellow.png';
                    }}
                  />
                </span>
                <span className="all-apps-internal__title">{app.title}</span>
              </button>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
